<script setup lang="ts">
/**
 * AiCharacter · AI 角色（人物，不是宠物）
 * ------------------------------------------------------------
 * 可扩展的「状态小剧场」渲染器：角色随账号在线状态（portalPresence）
 * 切换活动场景。场景是自包含组件（scenes/ 注册表），自带舞台——窗台只是
 * 其中一种，工位、走廊也行——外壳只负责：场景解析、说话气泡与节奏、
 * 点击蹦跳、悬停兴奋态、状态切换复位。
 *
 * - mode="dock"（默认）：顶栏坞站——按钮形制，点击进入「打工人」页面
 *   （系统 worker 总览），角色自己搭腔；
 * - mode="float"：悬浮形制——供 AiBuddy 复用（可拖拽），点击只蹦跳并
 *   emit('poke')，说话交给悬浮壳的技巧/鸡汤气泡，避免双层气泡。
 *
 * 新增场景：scenes/ 下加组件 + index.ts 注册一行，不用改这里。
 */
import { computed, markRaw, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { useRouter } from 'vue-router'
import { portalPresence, PRESENCE_LABEL } from '../portal/portal'
import { assistantProfile } from '@/shared/assistant'
import { rollScene } from './scenes'
import { useAiActivity } from './activity'
import type { AiScene } from './types'

const props = withDefaults(defineProps<{ mode?: 'dock' | 'float' }>(), { mode: 'dock' })
const emit = defineEmits<{ poke: [] }>()

const presence = portalPresence
const router = useRouter()
/* 同一状态下有多个场景变体：状态切换时随机抽，说话循环里偶尔也换一出 */
const scene = shallowRef<AiScene>(markRawScene(rollScene(presence.value)))
/** 把场景对象里的 comp 组件 markRaw，避免 Vue 把整个场景包成 reactive 造成性能警告 */
function markRawScene(s: AiScene): AiScene { s.comp = markRaw(s.comp); return s }
const dock = computed(() => props.mode === 'dock')
/* 单根节点（button/div 二选一）：保住 attribute 透传（AiBuddy 往角色上挂拖拽事件） */
const tag = computed(() => (dock.value ? 'button' : 'div'))
/* AI 回答实时活动：生成期间小人念叨阶段、点击直达会话现场（dock；float 只读状态不跳转） */
const { current: aiAct, line: actLine } = useAiActivity()
/* 悬浮提示用用户设计的助手昵称（设置中心「助手偏好」），留空回落默认名「企管助手」 */
const assistantName = computed(() => assistantProfile.name || '企管助手')
const title = computed(() =>
  dock.value
    ? aiAct.value
      ? `${assistantName.value} 正在回答 · 点我去会话现场`
      : `${assistantName.value}（${PRESENCE_LABEL[presence.value]}）· 点我看看系统里的 worker 们`
    : 'AI 助手 · 按住拖动，点击换一条'
)
const ariaLabel = computed(() =>
  dock.value ? `${assistantName.value}入口（${PRESENCE_LABEL[presence.value]}）` : title.value
)

const line = ref('')
const talking = ref(false)
const tick = ref(0)
const hop = ref(false)
const peek = ref(false)
const hot = ref(false)
let loopT = 0
let talkOffT = 0
let peekOffT = 0

/* 说完一句停留一会儿再收起；间隔带随机，显得不那么机械 */
function say() {
  const pool = scene.value.lines
  line.value = pool[Math.floor(Math.random() * pool.length)]
  tick.value++
  talking.value = true
  window.clearTimeout(talkOffT)
  talkOffT = window.setTimeout(() => { talking.value = false }, 5200)
}
/* 探头（场景 meta 声明 peekable 才有）：蹦出舞台看一会儿再缩回去 */
function doPeek() {
  const pool = scene.value.peekLines ?? scene.value.lines
  line.value = pool[Math.floor(Math.random() * pool.length)]
  tick.value++
  talking.value = true
  peek.value = true
  window.clearTimeout(peekOffT)
  peekOffT = window.setTimeout(() => { peek.value = false }, 3400)
  window.clearTimeout(talkOffT)
  talkOffT = window.setTimeout(() => { talking.value = false }, 4400)
}
/* 自动说话循环（仅 dock；float 的说话由 AiBuddy 的技巧/鸡汤气泡负责）。
   每轮开口前小概率给当前状态换一出场景，久了也有新花样。
   AI 回答活动期跳过随机台词（气泡让位给念叨），只排下一轮。 */
function loop() {
  if (!aiAct.value) {
    if (Math.random() < 0.16) scene.value = markRawScene(rollScene(presence.value, scene.value))
    const sc = scene.value
    if (sc.peekable && Math.random() < 0.45) doPeek()
    else say()
  }
  const [gmin, gmax] = scene.value.gap ?? [9000, 17000]
  loopT = window.setTimeout(loop, gmin + Math.random() * (gmax - gmin))
}
/* 被戳：蹦一下；坞里在回答活动时直达会话现场，否则进入打工人页面，悬浮则通知外壳换一条 */
function onPoke() {
  hop.value = false
  window.requestAnimationFrame(() => {
    hop.value = true
    window.setTimeout(() => { hop.value = false }, 560)
  })
  if (!dock.value) { emit('poke'); return }
  const sessionId = aiAct.value?.sessionId
  if (sessionId) router.push({ path: '/chat', query: { session: sessionId } })
  else router.push('/workers')
}
/* 回答活动开始：收起舞台词与探头，气泡让位给念叨；结束：稍等片刻恢复随机台词循环 */
watch(aiAct, (now, before) => {
  if (now) {
    window.clearTimeout(talkOffT)
    window.clearTimeout(peekOffT)
    peek.value = false
    talking.value = false
  } else if (before && dock.value) {
    window.clearTimeout(loopT)
    loopT = window.setTimeout(loop, 1400)
  }
})
/* 念叨文案变化（阶段/工具/轮次切换）时重播气泡动画；同阶段刷新不重播 */
watch(actLine, () => { if (aiAct.value) tick.value++ })
/* 状态切换：清掉旧动作与台词，随机抽一出新场景，稍等片刻开场 */
watch(presence, () => {
  scene.value = markRawScene(rollScene(presence.value, scene.value))
  window.clearTimeout(loopT)
  window.clearTimeout(peekOffT)
  window.clearTimeout(talkOffT)
  talking.value = false
  peek.value = false
  line.value = ''
  if (dock.value) loopT = window.setTimeout(loop, 1400)
})
onMounted(() => { if (dock.value) loopT = window.setTimeout(loop, 2800) })
onBeforeUnmount(() => {
  window.clearTimeout(loopT)
  window.clearTimeout(talkOffT)
  window.clearTimeout(peekOffT)
})
</script>

<template>
  <component :is="tag" class="ai-char" :class="[mode, { hop, hot, live: !!aiAct }]"
             :type="dock ? 'button' : undefined" :title="title" :aria-label="ariaLabel"
             @click="onPoke" @mouseenter="hot = true" @mouseleave="hot = false">
    <span class="ai-stage">
      <component :is="scene.comp" :peek="peek" :hot="hot" />
    </span>
    <span v-if="dock && (aiAct || talking)" :key="tick" class="ap-pop">{{ aiAct ? actLine : line }}</span>
  </component>
</template>

<style scoped>
/* 坞站（顶栏）：一格比舞台略大的活动空间 */
.ai-char {
  position: relative; width: 48px; height: 50px; flex: none;
  border: 1px solid transparent; border-radius: 12px; color: inherit;
  font-size: 0;
}
.ai-char:hover { background: var(--hover); border-color: var(--line); }
.ai-stage { position: absolute; inset: 0; display: block; }
/* 坞站（顶栏）：图标按钮改为「图标在上文字在下」后，视觉中心上移到图标一行；
   整格舞台上移 5px，让小人的脸与右侧图标保持同一水平线（不上移会显得低半头）。
   用 translate 属性而非 transform——被戳蹦跳（aiHop）动画的是 transform，两者叠加不冲突。
   float 形制没有 nav 行对齐问题，不上移；≤760px 标签隐藏后由 PortalHeader 复位。 */
.ai-char.dock .ai-stage { translate: 0 -5px; }

/* 悬浮（AiBuddy）：无边框形制；zoom 把场景内所有 px 值等比放大一档 */
.ai-char.float {
  width: auto; height: auto; border: 0; border-radius: 0;
  cursor: pointer; user-select: none; touch-action: none;
}
.ai-char.float:hover { background: none; border-color: transparent; }
.ai-char.float .ai-stage {
  position: relative; inset: auto;
  width: 48px; height: 50px; zoom: 2.3;
}

/* 被戳：整格舞台蹦一下 */
.ai-char.hop .ai-stage { animation: aiHop .54s cubic-bezier(.2, .9, .3, 1.4); }
@keyframes aiHop {
  0% { transform: none; }
  35% { transform: translateY(-5px) scale(1.05); }
  65% { transform: translateY(1px) scale(.99); }
  100% { transform: none; }
}

/* 搭腔气泡（仅坞站；浮在按钮下方，不挡交互） */
.ap-pop {
  position: absolute; top: calc(100% + 4px); left: 50%;
  transform: translateX(-50%);
  z-index: 6; padding: 5px 10px; border-radius: 9px;
  background: var(--accent); color: #fff;
  font-size: 12px; line-height: 1.5; white-space: nowrap;
  box-shadow: 0 4px 12px color-mix(in srgb, var(--accent) 32%, transparent);
  pointer-events: none;
  animation: apPopIn .24s cubic-bezier(.2, .9, .3, 1.3) both;
}
.ap-pop::before {
  content: ''; position: absolute; top: -4px; left: 50%; width: 8px; height: 8px;
  margin-left: -4px; transform: rotate(45deg);
  background: var(--accent); border-radius: 2px;
}
@keyframes apPopIn {
  from { opacity: 0; translate: 0 6px; }
  to { opacity: 1; translate: 0 0; }
}
/* 回答活动期：气泡轻微脉冲，提示「正在干活」 */
.ai-char.live .ap-pop {
  animation: apPopIn .24s cubic-bezier(.2, .9, .3, 1.3) both, apPulse 1.6s ease-in-out .24s infinite;
}
@keyframes apPulse {
  0%, 100% { box-shadow: 0 4px 12px color-mix(in srgb, var(--accent) 32%, transparent); }
  50% { box-shadow: 0 4px 18px color-mix(in srgb, var(--accent) 58%, transparent); }
}
</style>
